<template>
  <div v-if="open" class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" @click.self="$emit('cancel')">
    <div class="bg-white rounded-lg shadow-xl w-full max-w-md p-5">
      <h2 class="text-lg font-semibold mb-1">
        {{ bulk ? `Prepare ${count} follow-up${count === 1 ? '' : 's'}` : 'Reply' }}
      </h2>
      <p class="text-sm text-gray-500 mb-4">
        {{ bulk ? `Create ${count} follow-up draft${count === 1 ? '' : 's'}` : 'Create a draft' }}
        replying within {{ bulk && count > 1 ? 'their' : 'its' }} thread.
      </p>

      <label class="block text-xs font-medium text-gray-600 mb-1">Start from</label>
      <select v-model="selected" class="w-full border rounded-md p-2 text-sm bg-white">
        <option v-if="bulk" value="" disabled>Choose a template…</option>
        <!-- Writing from scratch is single-reply only: a bespoke answer is
             written per conversation, which is the opposite of a bulk action. -->
        <option v-if="!bulk" :value="BESPOKE">Nothing — write it myself</option>
        <!-- Reusing the original's template is deliberately not offered: the
             original is an intro, so replying with it re-sends the intro. That
             holds in bulk too, just at scale. -->
        <option v-for="t in templates" :key="t.id" :value="t.id">{{ t.email_template_name }}</option>
      </select>
      <p class="text-xs text-gray-400 mt-1">
        Sends from the original's mailbox either way, and carries that mailbox's signature.
      </p>

      <div class="flex justify-end gap-2 mt-5">
        <button
          type="button"
          @click="$emit('cancel')"
          class="px-3 py-1.5 rounded-md text-sm border border-gray-300 bg-white hover:bg-gray-100"
        >Cancel</button>
        <button
          type="button"
          @click="confirm"
          :disabled="submitting"
          class="px-3 py-1.5 rounded-md text-sm bg-dark-primary text-white disabled:opacity-40"
        >{{ submitting ? 'Creating…' : (bulk ? `Create ${count}` : 'Create') }}</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, defineProps, defineEmits } from 'vue'
import { useStore } from 'vuex'
import { useToast } from 'vue-toast-notification'
import { getEmailTemplates } from '@/services/EmailTemplates'

const props = defineProps({
  open: { type: Boolean, default: false },
  count: { type: Number, default: 1 },
  // A bulk action, whatever the count. Deciding by count alone offered "write
  // it myself" to a bulk selection of one, which the bulk endpoints can't do.
  bulk: { type: Boolean, default: false },
  submitting: { type: Boolean, default: false },
})
const emit = defineEmits(['confirm', 'cancel'])

// Sentinel for "no template, write it from scratch". Callers branch on it and
// hit the reply endpoint instead of the follow-up one.
const BESPOKE = 'bespoke'

const store = useStore()
const $toast = useToast()
const templates = ref([])
const selected = ref('') // '' = nothing picked yet
let loaded = false

async function loadTemplates() {
  if (loaded) return
  try {
    templates.value = await getEmailTemplates(store.state.auth.token) || []
    loaded = true
  } catch (e) {
    $toast.error('Failed to load templates')
  }
}

// Load lazily the first time the modal opens; reset the choice each open.
watch(() => props.open, (isOpen) => {
  if (isOpen) {
    // A single reply starts blank: answering what someone said is the common
    // case and no template fits it. Bulk has no blank option -- writing each
    // one by hand is the opposite of a bulk action -- so it starts unpicked and
    // the analyst must choose a template.
    selected.value = props.bulk ? '' : BESPOKE
    loadTemplates()
  }
})

function confirm() {
  if (!selected.value) {
    $toast.error('Pick a template')
    return
  }
  // BESPOKE -> write from scratch; anything else -> that template's id.
  emit('confirm', selected.value)
}
</script>
